export const CHECKOUT_URL = "https://ecudecision-en.impultienda.ar/checkout/";

export const images = {
  mockup: "https://cdn.magicpatterns.com/uploads/ieHFnLYyg2X6pCdsqh27Gd/ECUD-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/k3W33gTG9evwdij21zC6UV/JASON_MITCHELL_-_ECU_DECISION.png",
};

export const workflow = ["Identify", "Classify", "Diagnose", "Select", "Prepare", "Verify"];

export const technicianAssets = [
  "years of automotive experience",
  "expensive diagnostic equipment",
  "several programmers",
  "hundreds of saved files",
  "dozens of tutorials",
];

export const intakeQuestions = [
  "what information to collect?",
  "what you should check first?",
  "which tool category makes sense?",
  "what needs to be backed up?",
  "what should be verified before proceeding?",
  "what to check when communication fails?",
];

export const beforeItems = [
  { title: "You spend too much time figuring out where to start.", text: "You search through videos, forums and old notes trying to find something relevant to the ECU in front of you." },
  { title: "You aren't always sure which tool to use.", text: "You may own several programmers, but selecting the right tool can still take unnecessary research." },
  { title: "Every unfamiliar ECU feels like starting from zero.", text: "Different manufacturers, hardware, memory types and procedures create uncertainty." },
  { title: "You worry about making the wrong move.", text: "When you're dealing with expensive electronic modules, guessing isn't a comfortable strategy." },
  { title: "You depend on other technicians for answers.", text: "Sometimes the fastest way to solve the problem is to message someone who has already seen it." },
  { title: "You turn down jobs because you're not completely confident.", text: "And every job you turn away is an opportunity your workshop doesn't get to monetize." },
];

export const afterItems = [
  { title: "You identify the ECU systematically.", text: "You know what information matters before moving forward." },
  { title: "You follow a structured diagnostic path.", text: "Instead of randomly testing possibilities, you work through a logical decision process." },
  { title: "You make better tool decisions.", text: "You match the ECU, memory type and objective with the appropriate tool category." },
  { title: "You use professional checklists.", text: "You don't have to rely entirely on memory." },
  { title: "You organize your files and documentation.", text: "Originals, backups, notes and verification records follow a consistent structure." },
  { title: "You develop a repeatable workflow.", text: "The goal isn't to memorize everything. The goal is to know what to check next." },
];

export const situations = [
  { title: "An ECU arrives at your workshop and you're not completely sure where to begin.", text: "You know there's a process, but you don't want to waste an hour figuring out what information matters first." },
  { title: "You spend too much time searching for information before starting a job.", text: "You have hundreds of resources, but finding the right one is often the hardest part." },
  { title: "You own multiple tools but aren't always sure which one fits the job.", text: "You don't want to buy another programmer just because someone on the internet recommended it." },
  { title: "You know there are higher-value ECU services you could offer, but uncertainty makes you hesitate.", text: "You would rather have a repeatable process before taking on more complicated work." },
  { title: "You're worried about making a mistake during an important job.", text: "You want a structured preparation and verification process before moving forward." },
  { title: "Every unfamiliar ECU forces you to start researching from zero.", text: "You want a framework you can reuse instead of reinventing your process every time." },
];

export const gapLines = [
  "You can keep buying courses.",
  "You can keep watching tutorials.",
  "You can keep downloading PDFs.",
  "You can keep saving YouTube videos.",
];

export type Volume = {
  numeral: string;
  title: string;
  subtitle: string;
  intro: string;
  flow?: string[];
  steps?: string[];
  items?: string[];
  outro: string;
};

export const volumes: Volume[] = [
  {
    numeral: "I",
    title: "The ECU Identification System™",
    subtitle: "Know what you're looking at before you touch it",
    intro: "Before you can make a good decision, you need to know exactly what you're dealing with. A structured method for collecting and organizing the information that matters:",
    items: ["Manufacturer", "Part number", "Hardware number", "Software number", "ECU category", "Memory type", "Microcontroller info", "Vehicle info", "Connector info", "Previous repairs"],
    outro: "You'll build an ECU Information Profile before choosing your next step.",
  },
  {
    numeral: "II",
    title: "The ECU Diagnostic Decision Tree™",
    subtitle: "Stop wondering where to start",
    intro: "One of the core parts of the system. Instead of asking “What should I try?” you'll work through:",
    flow: ["Symptom", "Information", "Possible cause", "Diagnostic path", "Next action"],
    items: ["No communication", "Reading problems", "Unexpected data", "Programming errors", "Connection issues", "Power-related problems", "Post-programming verification"],
    outro: "The goal is simple: give yourself a logical next step.",
  },
  {
    numeral: "III",
    title: "The ECU Tool Selection Matrix™",
    subtitle: "Know which tool category fits the job before you connect anything",
    intro: "If you've ever looked at UPA, K-TAG, KESS, VVDI, XPROG, TL866 and other tools and wondered “Which one do I actually need?” — you'll learn to think through:",
    flow: ["ECU type", "Memory", "Objective", "Connection method", "Tool category"],
    outro: "The purpose isn't to collect every tool. The purpose is to make better tool decisions.",
  },
  {
    numeral: "IV",
    title: "The Safe ECU Workflow™",
    subtitle: "The correct order from intake to verification",
    intro: "A repeatable workflow you can follow on every job:",
    steps: ["Identify", "Document", "Prepare", "Backup", "Analyze", "Execute", "Verify", "Record"],
    outro: "Includes professional checklists designed to help you avoid skipping important preparation and verification steps.",
  },
  {
    numeral: "V",
    title: "The ECU Troubleshooting Playbook™",
    subtitle: "Know what to check when things don't go according to plan",
    intro: "Not every ECU job goes perfectly. Sometimes:",
    items: ["Communication fails", "Reading fails", "Unstable connection", "The file doesn't look right", "Programming doesn't complete", "Unexpected behavior afterward"],
    outro: "Panic isn't a diagnostic method. You'll get structured troubleshooting paths that help you isolate the problem before assuming the worst.",
  },
  {
    numeral: "VI",
    title: "The ECU Workshop System™",
    subtitle: "Turn your technical knowledge into a repeatable professional process",
    intro: "This volume turns the technical workflow into a workshop system. You'll organize:",
    items: ["ECU intake", "Customer information", "Job documentation", "File organization", "Original backups", "Diagnostic notes", "Verification", "Final reports", "Job history"],
    outro: "I don't want you to simply become better at ECU work. I want you to become more organized and consistent doing it.",
  },
];

export const extras = [
  { title: "30+ ECU Decision Protocols", text: "Visual frameworks to move from a symptom or ECU identification to the appropriate next diagnostic step." },
  { title: "Professional ECU Checklists", text: "Pre-work, backup, diagnostic, programming and verification checklists." },
  { title: "Tool Selection Matrices", text: "Quick-reference tables to evaluate which tool category makes sense for different ECU scenarios." },
  { title: "ECU File Organization System", text: "A practical structure for original files, backups, versions, vehicle information and job records." },
  { title: "Visual ECU Decision Maps", text: "One-page visual references designed to be consulted directly from your workbench." },
];

export const bonuses = [
  {
    number: 1,
    title: "ECU Quick Reference Cards™",
    value: 47,
    image: "https://cdn.magicpatterns.com/uploads/8N81dFKQ2tcfFyaxGSztDZ/BONUS_1_ECUD-EN.png",
    text: "For the moments when you don't need another chapter — you need a quick answer. Visual reference cards covering key ECU concepts, memory types, diagnostic steps, preparation and verification.",
    note: "Print them. Keep them next to your bench. Save them on your phone.",
  },
  {
    number: 2,
    title: "The 10-Minute ECU Diagnostic Protocol™",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/iKsqVZvmwpigkhry1umv1c/BONUS_2_ECUD-EN.png",
    text: "A rapid framework to organize the initial ECU information, perform the basic checks, verify communication and determine your next diagnostic direction in approximately 10 minutes.",
    note: "The goal is to make sure those first 10 minutes are structured instead of random.",
  },
  {
    number: 3,
    title: "The ECU Troubleshooting Error Library™",
    value: 47,
    image: "https://cdn.magicpatterns.com/uploads/xce8rfe19cRoWx2MRHSuCB/BONUS_3_ECUD-EN.png",
    text: "When something goes wrong, don't immediately assume the ECU is dead. Common situations organized as Symptom → Possible causes → Check → Decision → Next step.",
    note: "Approach troubleshooting systematically instead of jumping from one theory to another.",
  },
  {
    number: 4,
    title: "Workshop ECU Documentation Kit™",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/11hXQGR5fJEgdSwFvsYaCP/BONUS_4_ECUD-EN.png",
    text: "Ready-to-use templates for ECU intake, job documentation, diagnostic reports, file backup records, verification, customer delivery and technician notes.",
    note: "Technical work becomes much easier to manage when your documentation is organized.",
  },
  {
    number: 5,
    title: "30 ECU Workbench Checklists™",
    value: 29,
    image: "https://cdn.magicpatterns.com/uploads/fB2rJXZZtZcsnMX8poMH32/BONUS_5_ECUD-EN.png",
    text: "Thirty printable checklists covering different stages of ECU work. Use them before, during and after your jobs so important steps don't depend entirely on memory.",
    note: "On your bench. In your binder. On your phone. Wherever you actually work.",
  },
];

export const testimonials = [
  {
    name: "Mike R.",
    country: "USA",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/b2b65f65-7099-41a0-af0d-a76b8ac75a9d.jpg",
    quote: "I had plenty of information saved on my computer, but I didn't have a process. The decision trees helped me organize the way I approach an ECU job.",
  },
  {
    name: "Carlos M.",
    country: "Mexico",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/41ff8822-1e1c-47c9-9563-897037980430.jpg",
    quote: "The tool matrix was one of my favorite parts. It helped me stop looking at programmers as a collection of tools and start thinking about what each one is actually for.",
  },
  {
    name: "Andrés P.",
    country: "Colombia",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/db7ad827-e9b5-4ec9-b477-72ee75385f02.jpg",
    quote: "The checklists are what I was missing. I now have a consistent process before starting a job instead of relying completely on memory.",
  },
  {
    name: "Antonio L.",
    country: "Spain",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/d399f862-f1bb-47a5-b873-62e3241bf33d.jpg",
    quote: "It doesn't feel like another long course. It's something I can actually keep next to the workbench and consult when I need it.",
  },
  {
    name: "Danielle W.",
    country: "USA",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/5667e9a7-e795-433e-a7e7-16587d99c279.jpg",
    quote: "The biggest difference for me was having a process. When I don't know the answer immediately, I know where to start looking.",
  },
];

export const offerItems = [
  { label: "ECU Decision System™ — 6 Premium Volumes", value: 97 },
  { label: "5 Exclusive Professional Bonuses", value: 197 },
];

export const offerPerks = ["One-time payment", "Lifetime access", "No monthly fees", "Instant download", "7-day guarantee"];

export const guaranteeSteps = ["Go through the decision trees.", "Look at the tool matrices.", "Print the checklists.", "Try the workflow."];

export const faqs = [
  { q: "How will I receive the material?", a: "Immediately after payment, you'll receive access instructions to download ECU Decision System™ and all five bonuses in digital format." },
  { q: "Is this a video course?", a: "No. ECU Decision System™ is primarily a digital reference and decision system: premium volumes, visual decision maps, checklists, matrices, protocols and workshop tools you can actually consult while working." },
  { q: "Is this for beginners?", a: "It's particularly useful for automotive technicians, mechanics, auto electricians and developing ECU technicians who already have a basic understanding of automotive electronics. It isn't intended to replace foundational electronics education or hands-on training." },
  { q: "Do I need UPA, K-TAG, KESS, VVDI, XPROG or TL866?", a: "No. The system teaches you how to think about tool selection. The correct equipment depends on the specific ECU, vehicle, manufacturer, task and applicable technical procedure." },
  { q: "Does this teach me how to bypass vehicle security systems?", a: "No. The system focuses on legitimate diagnostics, memory handling, backup, documentation, programming workflows and repair-oriented decision making. It does not provide instructions intended to defeat vehicle anti-theft or security protections." },
  { q: "Can I use it with different vehicle brands?", a: "Yes. The core framework is built around decision-making principles and workflows rather than one manufacturer. For manufacturer-specific procedures, always verify the appropriate technical documentation." },
  { q: "Are the files downloadable?", a: "Yes. You'll receive the materials digitally and can download them for personal use." },
  { q: "Is this a monthly subscription?", a: "No. It's a one-time payment of $27 USD with lifetime access to the purchased materials." },
  { q: "What if I don't like it?", a: "You have a 7-day guarantee. Open the volumes, review the decision trees, use the checklists. If it isn't right for your workflow, request your refund within the applicable 7-day period according to the refund terms." },
  { q: "What currency will I pay in?", a: "The price is shown in USD. The currency conversion is automatic — at checkout you'll see the equivalent amount in your local currency." },
];
